import { Icon, useDisclosure } from '@chakra-ui/react';
import {
  IUser,
  IUserInviteLink,
  IUserRole,
  UserRoleEnum,
  UserStatusEnum,
} from '@monetize/types/app';
import {
  DEFAULT_PAGER,
  FilterType,
  GetListApiFilter,
  TDataTablePager,
} from '@monetize/types/common';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdLink } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { usePerformEntityAction } from '../../../api/queryUtils';
import { useGetTenantById } from '../../../api/tenantService';
import {
  doGetInviteLink,
  useAddOrDeleteUserRoles,
  useCancelOrResendInvite,
  useGetUserRoles,
  useGetUsersListByTenantId,
} from '../../../api/usersService';
import {
  MBox,
  MButton,
  MFlex,
  MPageContainer,
  MPageLoader,
  MPageSearchInput,
  MSettingsPageHeader,
  MSpinner,
  MText,
} from '../../../components/Monetize';
import DataTableActions, {
  ActionType,
} from '../../../components/Monetize/DataTable/MDataTableActions';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { MClipboardCopyBox } from '../../../components/Monetize/MClipboardCopyBox';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import InviteForm, {
  handleTransformRoleLabel,
} from '../../../components/Users/InviteForm';
import { ROUTES, USERS } from '../../../constants';
import { USER_ROLES_DISPLAY } from '../../../constants/roles';
import { useACL } from '../../../services/acl/acl';
import { useAuth } from '../../../services/auth0';
import { useDocumentHead } from '../../../services/documentHead';
import { useFlags } from '../../../services/launchDarkly';
import { useToast } from '../../../services/toast';
import { tableFilterSelector } from '../../../store/global.store';
import { FilterStateKeys } from '../../../store/store.types';
import { buildFilterParamsRequestObject } from '../../../utils';
import { sendResetPassword } from '../../../utils/auth0';
import {
  MANAGE_USERS_LIST_CATALOG,
  ManageUsersListColumnContext,
} from './manageUsersList.columns';
import { UserRoleDropdown } from './UserRoleDropdown';

const ROLE_PAGER: TDataTablePager = {
  ...DEFAULT_PAGER,
  sortField: 'name',
  sortOrder: 1,
};

interface IUserInviteLinkObject {
  userId: string;
  link?: IUserInviteLink;
}

export const ManageUsers = () => {
  const { metabaseReporting } = useFlags();
  const { setDocTitle } = useDocumentHead();
  const navigate = useNavigate();
  const [initialFilters, persistTableFilter] = useAtom(
    tableFilterSelector(FilterStateKeys.USER_LIST),
  );
  const { currentTenant, userId } = useAuth();
  const { canDo } = useACL();
  const canInviteUpdateRoles = canDo([
    ['users', 'create'],
    ['users', 'update'],
  ]);

  const tenantId = currentTenant?.id || '';
  const [pager, setPager] = useState<TDataTablePager>(DEFAULT_PAGER);
  const [filters, setFilters] = useState<FilterType[]>(initialFilters);

  const [globalFilter, setGlobalFilter] = useState<string>('');
  const [inviteLinks, setInviteLinks] = useState<IUserInviteLinkObject[]>([]);
  const [copyLink] = useState<string>('');
  const [searchKey, setSearchKey] = useState<string>('name');
  const [filterParams, setFilterParams] = useState<GetListApiFilter>(() =>
    buildFilterParamsRequestObject(filters, globalFilter, searchKey),
  );

  const {
    data: tenant,
    isLoading: isLoadingTenant,
    isError,
  } = useGetTenantById(tenantId, {
    enabled: !!tenantId,
    meta: {
      showErrorToast: true,
    },
  });

  const {
    open: isInviteModalOpen,
    onOpen: onOpenInviteModal,
    onClose: onCloseInviteModal,
  } = useDisclosure();

  const { addToast } = useToast();

  const {
    data: listData,
    isLoading: getUserListLoading,
    refetch,
  } = useGetUsersListByTenantId<IUser>({
    tenantId,
    config: pager,
    filters: filterParams,
    axiosConfig: { customXTenantId: tenantId },
  });

  const { data: userRoles, isLoading: roleLoading } = useGetUserRoles<
    IUserRole[]
  >(
    ROLE_PAGER,
    { name: { contains: globalFilter } },
    {
      axiosConfig: { customXTenantId: tenantId },
      select: (data) => {
        if (!Array.isArray(data?.content)) {
          return [];
        }
        return (
          data.content
            .map((role) => {
              return {
                roleName: handleTransformRoleLabel(role.name),
                ...role,
              };
            })
            // Require feature flag to view REPORTING role
            .filter(
              (role) =>
                role.name !== UserRoleEnum.REPORTING || metabaseReporting,
            )
            // FIXME: SALES_MANAGER is not implemented on backend, removing from list of selectable options
            // https://monetizenow-workspace.slack.com/archives/C01T5UN56P7/p1668629531475229
            .filter((role) => role.name !== UserRoleEnum.SALES_MANAGER)
        );
      },
    },
  );

  useEffect(() => {
    if (isError) {
      navigate(ROUTES.TENANT_LIST);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  useEffect(() => {
    persistTableFilter(filters);
  }, [filters, persistTableFilter]);

  useEffect(() => {
    setDocTitle('Settings', 'Users');
  }, []);

  useEffect(() => {
    setFilterParams(
      buildFilterParamsRequestObject(filters, globalFilter, searchKey),
    );
  }, [pager, globalFilter, filters, searchKey]);

  const doActivateDeactivateUser = usePerformEntityAction('tenantUser', {
    axiosConfig: { customXTenantId: tenantId },
    meta: { showErrorToast: true },
  });

  const doCancelOrResendInvite = useCancelOrResendInvite({
    meta: { showErrorToast: true },
  });

  const doAddOrDeleteUserRoles = useAddOrDeleteUserRoles({
    meta: { showErrorToast: true },
  });
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

  const loading = getUserListLoading || isLoadingTenant || roleLoading;

  const tableRef = useRef<DataTableRef>(null);
  const renderRefs = useRef({
    actionsTemplate: (_rowData: IUser): React.ReactNode => null,
    userStatusBodyTemplate: (_rowData: IUser): React.ReactNode => null,
    roleBodyTemplate: (_rowData: IUser): React.ReactNode => null,
  });

  const onChangeRole = async (user: IUser, ev: IUserRole[]) => {
    const previousRoleIds = new Set(user.roles.map(({ id }) => id));
    const idsToAdd: string[] = [];

    ev.forEach(({ id }) => {
      if (previousRoleIds.has(id)) {
        previousRoleIds.delete(id);
      } else {
        idsToAdd.push(id);
      }
    });

    const idsToRemove = Array.from(previousRoleIds);

    setUpdatingUserId(user.id || null);
    doAddOrDeleteUserRoles.mutate(
      {
        tenantId,
        userId: user.id || '',
        idsToAdd,
        idsToRemove,
        userRoles,
      },
      { onSettled: () => setUpdatingUserId(null) },
    );
  };

  const actionBodyTemplate = (rowData: IUser) => {
    const isCurrentUser = userId === rowData.id;

    const actions: ActionType[] = [
      {
        title: 'Activate',
        enabled:
          !isCurrentUser && rowData.status === UserStatusEnum.DEACTIVATED,
        action: () =>
          doActivateDeactivateUser.mutateAsync({
            id: rowData.id || '',
            action: 'activate',
          }),
      },
      {
        title: 'Resend Invite',
        enabled:
          !isCurrentUser &&
          canInviteUpdateRoles &&
          rowData.status === UserStatusEnum.INVITED,
        action: () =>
          doCancelOrResendInvite.mutateAsync({
            action: 'resend',
            userId: rowData.id || '',
          }),
      },
      {
        title: 'Cancel Invite',
        enabled:
          !isCurrentUser &&
          canInviteUpdateRoles &&
          rowData.status === UserStatusEnum.INVITED,
        color: 'danger',
        action: async () => {
          const data = await doCancelOrResendInvite.mutateAsync({
            action: 'cancel',
            userId: rowData.id || '',
          });
          if (data) {
            addToast({
              summary: 'Invitation Canceled',
              detail: `Invitation canceled for ${rowData.name}`,
              severity: 'info',
            });
          }
        },
      },
      {
        title: 'Reset Password',
        enabled: rowData.status === UserStatusEnum.ACTIVE,
        action: async () => {
          try {
            await sendResetPassword({ email: rowData.email });

            addToast({
              summary: 'Reset Password',
              detail: `A password reset email has been sent to ${rowData.email}`,
            });
          } catch (err) {
            addToast({
              summary: 'Reset Password',
              detail: `Failed to send reset email to ${rowData.email}`,
              severity: 'error',
            });
          }
        },
      },
      {
        title: 'Deactivate',
        enabled: !isCurrentUser && rowData.status === UserStatusEnum.ACTIVE,
        color: 'danger',
        confirmProps: {
          title: `Are you sure you want to deactivate "${rowData.name}"?`,
          description:
            'Once deactivated, the user will no longer be in the tenant.',
          yesButton: 'Deactivate',
        },
        action: () =>
          doActivateDeactivateUser
            .mutateAsync({
              id: rowData.id || '',
              action: 'deactivate',
            })
            // Errors are toasted globally via meta.showErrorToast — swallow the
            // rejection so the confirm modal's catch doesn't toast a second time
            .catch(() => {}),
      },
    ];

    return <DataTableActions actions={actions} />;
  };

  const userStatusBodyTemplate = (rowData: IUser) => {
    const link = inviteLinks.find((l) => l.userId === rowData.id);

    return (
      <MFlex>
        <MText truncate lineClamp={1} mr={3}>
          {USERS.USER_STATUS_DISPLAY_STRING[rowData.status]}
        </MText>
        {rowData.status === UserStatusEnum.INVITED && (
          <MClipboardCopyBox
            copyValue={copyLink}
            tooltipMessage="Copy to Clipboard"
            isDisabled={link && !link.link}
            onCopyContent={async ({
              copyToClipboard,
              setCopiedContent,
              setShowCopyTooltip,
            }: {
              copyToClipboard: (str: string) => void;
              setCopiedContent: (val: boolean) => void;
              setShowCopyTooltip: (val: boolean) => void;
            }) => {
              try {
                setShowCopyTooltip(false);
                const inviteLink = await getUserInviteLinks(rowData.id!);
                if (inviteLink?.link) {
                  copyToClipboard(inviteLink.link.url);
                  setCopiedContent(true);
                  setTimeout(() => {
                    setCopiedContent(false);
                  }, 1000);
                }
                setShowCopyTooltip(false);
              } catch (err) {
                addToast({
                  summary: 'Invitation link',
                  detail: `Failed to retrieve the link.`,
                  severity: 'error',
                });
              }
            }}
            renderContent={() => {
              const inviteLink = inviteLinks.find(
                (l) => l.userId === rowData.id!,
              );

              return inviteLink && !inviteLink.link ? (
                <MSpinner size="xs" />
              ) : (
                <Icon
                  verticalAlign="middle"
                  boxSize={3.5}
                  color="tPurple.dark"
                  cursor="pointer"
                  asChild
                >
                  <MdLink />
                </Icon>
              );
            }}
          />
        )}
      </MFlex>
    );
  };

  const roleBodyTemplate = (rowData: IUser) => {
    if (!rowData.roles) {
      return null;
    }

    // If it's current user
    if (userId === rowData.id || !canInviteUpdateRoles) {
      return (
        <MBox>
          <MText>
            {rowData.roles
              .map(
                (role) =>
                  USER_ROLES_DISPLAY[role.name as UserRoleEnum] || role.name,
              )
              .join(', ')}
          </MText>
        </MBox>
      );
    }

    return (
      <UserRoleDropdown
        user={rowData}
        userRoles={userRoles || []}
        onChangeRole={onChangeRole}
        isLoading={updatingUserId === rowData.id}
      />
    );
  };

  // Keep refs in sync so stable column defs always call the latest functions
  renderRefs.current.actionsTemplate = actionBodyTemplate;
  renderRefs.current.userStatusBodyTemplate = userStatusBodyTemplate;
  renderRefs.current.roleBodyTemplate = roleBodyTemplate;

  const columnContext = useMemo<ManageUsersListColumnContext>(
    () => ({
      renderRefs,
      actionBodyTemplate: (rowData: IUser) =>
        renderRefs.current?.actionsTemplate(rowData),
    }),
    [],
  );
  const { columns, state: columnState } = useTableColumns(
    MANAGE_USERS_LIST_CATALOG,
    columnContext,
  );

  if (isLoadingTenant) {
    return <MPageLoader />;
  }

  if (!tenant) {
    return null;
  }

  const handleUserSearch = (value: string) => {
    if (value.startsWith('usr_')) {
      setSearchKey('id');
    } else {
      setSearchKey('name');
    }
    setGlobalFilter(value);
  };

  const getUserInviteLinks = async (id: string) => {
    const link = inviteLinks.find((l) => l.userId === id);

    const linksArray = [...inviteLinks];
    if (!link) {
      linksArray.push({
        userId: id,
      });
      setInviteLinks(linksArray);
      const inviteLink = await doGetInviteLink(id);
      const linkIndex = linksArray.findIndex((l) => l.userId === id);
      const newLink = {
        userId: id,
        link: inviteLink,
      };

      if (linkIndex === -1) {
        linksArray.push(newLink);
      } else {
        linksArray.splice(linkIndex, 1, newLink);
      }

      setInviteLinks([...linksArray]);
      return {
        userId: id,
        link: inviteLink,
      };
    } else {
      return link;
    }
  };

  return (
    <MPageContainer data-testid="tenant-container">
      <MSettingsPageHeader title="Users">
        <MFlex>
          <MPageSearchInput
            placeholderKey="User Name or ID"
            value={globalFilter}
            count={listData?.totalElements}
            mode="alwaysExpanded"
            onChange={handleUserSearch}
          />
          <RefreshTableButton refetch={refetch} />
          <ExportEntityButton
            entity="users"
            filters={filters}
            searchKey={searchKey}
            searchTerm={globalFilter}
            sortField={pager.sortField}
            sortOrder={pager.sortOrder}
            endpointParams={[tenantId]}
          />
          <MButton
            variant="primary"
            ml="2"
            isDisabled={!canInviteUpdateRoles}
            onClick={onOpenInviteModal}
            data-testid="invite-users-btn"
          >
            Invite Users
          </MButton>
        </MFlex>
      </MSettingsPageHeader>

      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={listData?.content || []}
        isLoading={loading}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={listData?.totalElements || 0}
        totalPages={listData?.totalPages}
        pager={pager}
        setPager={setPager}
        initialFilters={filters}
        onFiltersChange={(newFilters) => {
          setFilters(
            newFilters.map((f) =>
              f.key === 'name' && String(f.value).startsWith('usr_')
                ? { ...f, key: 'id' }
                : f,
            ),
          );
        }}
        resetFilter={() => {
          setFilters([]);
          tableRef.current?.resetFilters();
        }}
        emptyProps={{
          mainMessage: 'Looks like there are no users here.',
        }}
      />

      <InviteForm
        userRoles={userRoles}
        isOpen={isInviteModalOpen}
        onClose={onCloseInviteModal}
        tenantId={tenantId}
      />
    </MPageContainer>
  );
};
